/**
 * «Avance de obra» (OPUS ci, idea 11 «Avance 4D»): el video de cómo fue avanzando la obra sobre el modelo 3D real.
 * Solo staff. Sin IA, $0.
 *
 * Los avances los ENVÍAN OPUS Presupuesto (OPUS 27: sus estimaciones, periodo por periodo) y OPUS Control (el avance en obra); aquí solo se
 * leen y se reproducen. Para cada fecha, cada concepto trae su avance acumulado; sus piezas del modelo se completan en
 * el orden en que se construyen (de abajo hacia arriba, de un lado al otro) y la que va a medias se ve crecer:
 * lo hecho sólido y lo que falta como fantasma. La lógica vive en @supernova/shared (`avanceDeObra`) y en
 * `lib/avanceObra4d`; el recorte por pieza, en `IfcViewer3D` (prop `avance4d`).
 *
 * Lo que no se pudo ligar a un concepto de la hoja no se esconde: queda en «Por revisar», con su motivo, y su importe
 * sí cuenta en la curva S.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Select,
  Spinner,
  makeStyles,
} from '@fluentui/react-components';
import { Location16Filled, Location16Regular } from '@fluentui/react-icons';
import {
  avanceDePiezasEn,
  conceptosParaPintar,
  importeAcumuladoEn,
  interpolar,
  nudosDeAvance,
  rangoDeFechas,
  seleccionesDe,
  type AvanceObraConcepto,
  type LeerAvanceObraResponse,
  type OrigenAvance,
  diaDeFecha,
} from '@supernova/shared';
import { ApiClientError } from '../../api/client.js';
import { getAvanceDePlano } from '../../api/avanceObra.js';
import {
  etiquetaDeDia,
  mapaParaElVisor,
  nombreDeLaObra,
  ordenDeConstruccion,
  piezasDwgDeHandles,
  textoDeMotivo,
  textoPorRevisar,
  type CajaDePieza,
} from '../../lib/avanceObra4d.js';
import { formatoDeMoneda } from '../../lib/money.js';
import { IfcViewer3D } from '../IfcViewer3D.js';
import { PlanoViewerMlightcad, type CensoDelPlano } from '../PlanoViewerMlightcad.js';
import { BarraDeReproduccion } from './BarraDeReproduccion.js';
import { CurvaS } from './CurvaS.js';
import { useReproduccionDeAvance } from './useReproduccionDeAvance.js';

interface Props {
  abierto: boolean;
  onCerrar: () => void;
  projectId: string;
  /** El archivo del plano: un modelo IFC (3D) o un dibujo DWG/DXF (2D). */
  fileId: string;
  /** Cómo se ve la obra: sobre el modelo 3D (por omisión) o sobre el dibujo 2D. */
  formato?: 'ifc' | 'dwg';
  /** Moneda de la obra (para los importes); sin ella, números sin símbolo. */
  moneda?: string | null;
}

const useEstilos = makeStyles({
  // Un concepto que SE PUEDE ubicar en el modelo: se nota que es un botón (borde, cursor, resalte al pasar y al enfocar).
  filaUbicable: {
    cursor: 'pointer',
    border: '1px solid var(--border-subtle)',
    ':hover': { backgroundColor: 'var(--bg-raised)', border: '1px solid var(--accent)' },
    ':focus-visible': { outline: '2px solid var(--accent)', outlineOffset: '1px' },
  },
});

/** «12 oct 2026»: cuándo llegó el avance (para distinguir una foto vieja de la vigente). */
const fechaDeRecepcion = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
};

const NOMBRE_DE_ORIGEN: Record<OrigenAvance, string> = {
  m1: 'OPUS Presupuesto (estimaciones)',
  m2: 'OPUS Control (avance en obra)',
};

// Los colores del dibujo 2D (el visor 2D los toma de su modo de comparación: «nuevo» = construido, «cambió» = en proceso, «quitado» = el
// concepto que la persona marcó en el panel).
const COLORES_2D = { added: 0x2ea043, modified: 0x3b82f6, deleted: 0xff2bd6, base: 0x6b737c };
const SIN_RENGLONES: never[] = [];
const SIN_RESALTE = { capas: [] as string[], bloques: [] as string[], rol: 'added' as const };
const NADA = () => undefined;

export function AvanceDeObra({ abierto, onCerrar, projectId, fileId, formato = 'ifc', moneda }: Props) {
  const esDwg = formato === 'dwg';
  const [censo, setCenso] = useState<CensoDelPlano | null>(null);
  const [datos, setDatos] = useState<LeerAvanceObraResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  // La fuente que la persona ELIGIÓ con el selector (null = la que el servidor ponga por omisión). La respuesta no la cambia:
  // si no, cada apertura leería dos veces.
  const [fuente, setFuente] = useState<{ obraRef: string; origen: OrigenAvance } | null>(null);
  const [cajas, setCajas] = useState<ReadonlyMap<number, CajaDePieza> | null>(null);
  // El concepto elegido en el panel (su `refExterna`): sus piezas se marcan en el modelo.
  const [conceptoElegido, setConceptoElegido] = useState<string | null>(null);

  // ----- los datos
  useEffect(() => {
    if (!abierto) return;
    let vivo = true;
    setCargando(true);
    setError(null);
    getAvanceDePlano(projectId, fileId, fuente ?? {})
      .then((r) => {
        if (!vivo) return;
        setDatos(r);
      })
      .catch((e: unknown) => {
        if (vivo) setError(e instanceof ApiClientError ? e.message : 'No se pudo leer el avance de la obra. Intenta de nuevo.');
      })
      .finally(() => {
        if (vivo) setCargando(false);
      });
    return () => {
      vivo = false;
    };
  }, [abierto, projectId, fileId, fuente?.obraRef, fuente?.origen]);

  // Al cerrar se limpia todo: la próxima vez arranca de cero.
  useEffect(() => {
    if (abierto) return;
    setDatos(null);
    setCajas(null);
    setCenso(null);
    reiniciar();
    setFuente(null);
    setConceptoElegido(null);
    setError(null);
  }, [abierto]);

  // La moneda de la hoja viene con la lectura; la que pasa quien monta el diálogo es solo el respaldo.
  const fmt = useMemo(() => formatoDeMoneda(datos?.moneda ?? moneda), [datos?.moneda, moneda]);
  const rango = useMemo(() => (datos ? rangoDeFechas(datos.periodos) : null), [datos]);
  const { t, reproduciendo, velocidad, setVelocidad, saltarA, pausar, alternar, reiniciar } = useReproduccionDeAvance(rango);

  // ----- lo que se pinta
  // En un dibujo DWG el servidor no trae piezas: salen del dibujo ya leído. Un concepto de BLOQUE trae los handles de sus bloques; uno de
  // CAPA, todas las entidades de esa capa (en su planta, si la trae). Se ordenan de izquierda a derecha y de abajo hacia arriba del dibujo
  // (el orden en que «se construyen»), cada una pesa lo que mide en el dibujo (la diagonal de su caja), para que el porcentaje pintado se parezca al real.
  const conceptos = useMemo<AvanceObraConcepto[]>(() => {
    if (!datos) return [];
    if (!esDwg) return datos.conceptos;
    if (!censo) return datos.conceptos.map((c) => ({ ...c, piezas: [] }));
    return datos.conceptos.map((c) => {
      const handles = new Set<string>();
      for (const s of seleccionesDe(c)) {
        const hs = s.tipo === 'bloque' ? (s.ids && s.ids.length > 0 ? s.ids : censo.handlesDeBloque(s.clave)) : censo.handlesDeCapa(s.clave, s.vista ?? null);
        for (const h of hs) handles.add(h);
      }
      return { ...c, piezas: piezasDwgDeHandles(handles, censo) };
    });
  }, [datos, esDwg, censo]);
  const modeloListo = esDwg ? censo != null : cajas != null;
  const pintables = useMemo(
    () =>
      datos && modeloListo
        ? conceptosParaPintar(datos.periodos, conceptos, esDwg ? (p) => p : (p) => ordenDeConstruccion(p, cajas!))
        : [],
    [datos, conceptos, modeloListo, esDwg, cajas],
  );
  const estadoDePiezas = useMemo(() => (t != null ? avanceDePiezasEn(pintables, t) : new Map()), [pintables, t]);
  // Con el modelo ya leído, SIEMPRE hay mapa (aunque esté vacío): así lo que no tiene avance se ve tenue y no «terminado».
  const avance4d = useMemo(() => (t != null && cajas ? mapaParaElVisor(estadoDePiezas) : null), [estadoDePiezas, cajas, t]);
  const sinNadaQuePintar = modeloListo && pintables.length === 0;
  // Las piezas del concepto elegido (para marcarlas en el modelo); un concepto sin piezas no se puede ubicar.
  const piezasDelElegido = useMemo(() => {
    const c = conceptoElegido ? conceptos.find((x) => x.refExterna === conceptoElegido) : undefined;
    return c && c.piezas.length > 0 ? c.piezas : null;
  }, [conceptoElegido, conceptos]);
  const resaltadas = useMemo(() => (!esDwg && piezasDelElegido ? new Set(piezasDelElegido.map((p) => Number(p.id))) : null), [esDwg, piezasDelElegido]);
  // El dibujo 2D: lo construido en verde, lo que va a medias en azul, el concepto marcado en magenta y el resto tenue.
  // Con todo construido (y nada marcado) el dibujo se ve tal cual, con sus colores, como en el visor: el recoloreo solo aparece mientras hay
  // algo que distinguir.
  const dibujoCompleto = useMemo(() => {
    if (!esDwg || piezasDelElegido || estadoDePiezas.size === 0) return false;
    for (const x of estadoDePiezas.values()) if (x.estado !== 'terminada') return false;
    return true;
  }, [esDwg, piezasDelElegido, estadoDePiezas]);
  const rolesDwg = useMemo(() => {
    if (!esDwg || dibujoCompleto) return null;
    const m = new Map<string, 'added' | 'deleted' | 'modified'>();
    for (const [id, x] of estadoDePiezas) {
      if (x.estado === 'terminada') m.set(id, 'added');
      else if (x.estado === 'en_proceso') m.set(id, 'modified');
    }
    for (const p of piezasDelElegido ?? []) m.set(p.id, 'deleted');
    return m;
  }, [esDwg, dibujoCompleto, estadoDePiezas, piezasDelElegido]);
  const conteo = useMemo(() => {
    let terminadas = 0, enProceso = 0, pendientes = 0;
    for (const x of estadoDePiezas.values()) {
      if (x.estado === 'terminada') terminadas++;
      else if (x.estado === 'en_proceso') enProceso++;
      else pendientes++;
    }
    return { terminadas, enProceso, pendientes };
  }, [estadoDePiezas]);

  // ----- el panel
  // El porcentaje de la obra se mide contra TODO el presupuesto de la hoja, no contra lo que ya trae avance (con una obra a medias eso
  // diría «87 %» cuando va en 34 %). Si el servidor no lo trae, se cae a lo contratado de los conceptos con avance.
  const contratadoTotal = useMemo(() => {
    if (!datos) return 0;
    if (datos.presupuestoTotal != null && datos.presupuestoTotal > 0) return datos.presupuestoTotal;
    return datos.conceptos.reduce((s, c) => s + c.contratado * c.precioUnitario, 0);
  }, [datos]);
  const filas = useMemo(
    () =>
      datos
        ? conceptos
            .filter((c) => c.conceptoId !== null)
            .map((c) => ({ c, nudos: nudosDeAvance(datos.periodos, new Map(c.porPeriodo.map((p) => [p.numero, p.pct] as const))) }))
            // En el orden en que empiezan a ejecutarse (el primer día con avance); los que no empiezan, al final, en su orden de la hoja.
            .map((f, i) => ({ ...f, i, inicio: f.nudos.find((n) => n.v > 0)?.t ?? Infinity }))
            .sort((a, b) => (a.inicio === b.inicio ? a.i - b.i : a.inicio - b.inicio))
        : [],
    [datos, conceptos],
  );
  const porRevisar = useMemo(() => (datos ? datos.conceptos.filter((c) => c.estadoVinculo === 'por_revisar') : []), [datos]);
  const ejecutado = datos && t != null ? importeAcumuladoEn(datos.periodos, t) : 0;
  const pctObra = contratadoTotal > 0 ? ejecutado / contratadoTotal : 0;

  const sinAvances = !cargando && !error && datos != null && datos.seleccionada === null;
  const fuentesDistintas = datos ? datos.fuentes.length > 1 : false;

  return (
    <Dialog open={abierto} onOpenChange={(_, d) => { if (!d.open) onCerrar(); }}>
      <DialogSurface style={{ width: 1280, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 32px)' }}>
        <DialogBody style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <DialogTitle>Avance de obra</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden' }}>
            {error && (
              <MessageBar intent="error">
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            )}
            {sinAvances && (
              <MessageBar intent="info">
                <MessageBarBody>
                  Este plano todavía no tiene avances de obra. Llegan desde OPUS Presupuesto (las estimaciones de la obra) o desde OPUS Control (el avance en obra),
                  cuando se envían de allá.
                </MessageBarBody>
              </MessageBar>
            )}
            {cargando && !datos && <Spinner label="Leyendo el avance de la obra…" />}

            {datos?.seleccionada && rango && t != null && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 16, minHeight: 0 }}>
                  {/* El modelo */}
                  <div
                    style={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      height: 'calc(100vh - 345px)',
                      minHeight: 380,
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-sm, 4px)',
                      overflow: 'hidden',
                      background: 'var(--bg-surface)',
                    }}
                  >
                    {esDwg ? (
                      <PlanoViewerMlightcad
                        projectId={projectId}
                        fileId={fileId}
                        renglones={SIN_RENGLONES}
                        comparacionPorEntidad={rolesDwg}
                        resaltarCambio={dibujoCompleto ? null : SIN_RESALTE}
                        coloresComparacion={COLORES_2D}
                        onCenso={setCenso}
                        onSeleccion={NADA}
                        sinControles
                      />
                    ) : (
                      <IfcViewer3D projectId={projectId} fileId={fileId} sinBarraDeEstado avance4d={avance4d} avance4dResaltadas={resaltadas} onCajasDePiezas={setCajas} />
                    )}
                    <div style={{ position: 'absolute', left: 10, bottom: 10, fontSize: 'var(--fs-200, 12px)', color: 'var(--text-secondary)', background: 'var(--bg-raised)', padding: '4px 8px', borderRadius: 4 }}>
                      {conteo.terminadas} terminadas · {conteo.enProceso} en proceso · {conteo.pendientes} por construir
                    </div>
                  </div>

                  {/* El panel */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0, overflowY: 'auto', maxHeight: 'calc(100vh - 345px)' }}>
                    <div style={{ flexShrink: 0 }}>
                      <div style={{ fontSize: 'var(--fs-200, 12px)', color: 'var(--text-secondary)' }}>{etiquetaDeDia(t)}</div>
                      <div style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.1 }}>{(pctObra * 100).toFixed(0)} % de la obra</div>
                      <div style={{ fontSize: 'var(--fs-200, 12px)', color: 'var(--text-secondary)' }}>
                        {fmt.moneyCorto(ejecutado)} ejecutados de {fmt.moneyCorto(contratadoTotal)} del presupuesto
                      </div>
                    </div>

                    {fuentesDistintas && datos && (
                      <div style={{ flexShrink: 0 }}>
                      <div style={{ fontSize: 'var(--fs-200, 12px)', color: 'var(--text-secondary)', marginBottom: 4 }}>
                        Este plano tiene avances de dos fuentes. Toca una para verla:
                      </div>
                      <div role="group" aria-label="Fuente del avance" style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(datos.fuentes.length, 2)}, 1fr)`, gap: 8 }}>
                        {datos.fuentes.map((f) => {
                          const activa = (fuente ?? datos.seleccionada)?.origen === f.origen && (fuente ?? datos.seleccionada)?.obraRef === f.obraRef;
                          return (
                            <button
                              key={`${f.origen}|${f.obraRef}`}
                              type="button"
                              aria-pressed={activa}
                              onClick={() => {
                                pausar();
                                setConceptoElegido(null);
                                setFuente({ origen: f.origen, obraRef: f.obraRef });
                              }}
                              style={{
                                textAlign: 'left', cursor: 'pointer', padding: '8px 10px', borderRadius: 8,
                                background: activa ? 'rgba(11,87,208,0.22)' : 'var(--bg-surface)',
                                color: 'var(--text-primary)',
                                border: `2px solid ${activa ? '#4c8df6' : 'var(--border-default, #3a4350)'}`,
                              }}
                            >
                              <div style={{ fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                <span
                                  aria-hidden
                                  style={{
                                    flex: 'none', width: 14, height: 14, marginTop: 2, borderRadius: '50%', boxSizing: 'border-box',
                                    border: `2px solid ${activa ? '#4c8df6' : 'var(--text-tertiary, #7a8494)'}`,
                                    background: activa ? '#4c8df6' : 'transparent',
                                    boxShadow: activa ? 'inset 0 0 0 2px var(--bg-surface, #0e1420)' : 'none',
                                  }}
                                />
                                <span>{NOMBRE_DE_ORIGEN[f.origen]}</span>
                              </div>
                              <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                                {f.hasta ? `Avance al ${etiquetaDeDia(diaDeFecha(f.hasta))}` : `Recibido el ${fechaDeRecepcion(f.recibidoAt)}`}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      </div>
                    )}
                    {!fuentesDistintas && (
                      <div style={{ fontSize: 'var(--fs-200, 12px)', color: 'var(--text-tertiary)' }}>
                        {NOMBRE_DE_ORIGEN[datos.seleccionada.origen]} · {nombreDeLaObra(datos.seleccionada.obraRef)} · recibido el {fechaDeRecepcion(datos.seleccionada.recibidoAt)}
                      </div>
                    )}

                    <div style={{ flexShrink: 0 }}>
                    <CurvaS
                      periodos={datos.periodos}
                      desde={rango.desde}
                      hasta={rango.hasta}
                      t={t}
                      contratado={contratadoTotal}
                      moneyCorto={fmt.moneyCorto}
                      onElegirFecha={saltarA}
                    />
                    </div>

                    <div style={{ flexShrink: 0 }}>
                      <div style={{ fontWeight: 600 }}>Conceptos</div>
                      {filas.length > 0 && (
                        <>
                          <div style={{ fontSize: 'var(--fs-200, 12px)', color: 'var(--text-secondary)', margin: '2px 0 6px' }}>
                            En el orden en que empiezan a ejecutarse. Toca uno con el ícono <Location16Regular aria-hidden style={{ verticalAlign: 'text-bottom' }} /> para ver
                            dónde está en el {esDwg ? 'dibujo' : 'modelo'}; los demás (trazo, trámites, instalaciones…) solo existen en el presupuesto, pero su avance sí
                            cuenta en el porcentaje de la obra y en la curva.
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {filas.map(({ c, nudos }) => (
                              <FilaDeConcepto
                                key={c.refExterna}
                                c={c}
                                pct={interpolar(nudos, t)}
                                elegido={conceptoElegido === c.refExterna}
                                onElegir={() => {
                                  // Marcar un concepto detiene la animación (por la misma razón: se vería a bloques).
                                  pausar();
                                  setConceptoElegido((x) => (x === c.refExterna ? null : c.refExterna));
                                }}
                              />
                            ))}
                          </div>
                        </>
                      )}
                      {filas.length === 0 && <div style={{ color: 'var(--text-secondary)' }}>Ningún concepto de este plano tiene avance todavía.</div>}
                    </div>

                    {sinNadaQuePintar && (
                      <MessageBar intent="warning" style={{ flexShrink: 0 }}>
                        <MessageBarBody>
                          Ninguno de los conceptos con avance tiene piezas en este modelo, así que no hay nada que construir en pantalla. El
                          importe y la curva sí se muestran.
                        </MessageBarBody>
                      </MessageBar>
                    )}

                    {porRevisar.length > 0 && (
                      <div style={{ flexShrink: 0 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>Por revisar ({porRevisar.length})</div>
                        <div style={{ fontSize: 'var(--fs-200, 12px)', color: 'var(--text-secondary)', marginBottom: 6 }}>
                          {textoPorRevisar(datos.seleccionada.origen)}
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {porRevisar.map((c) => (
                            <li key={c.refExterna} style={{ fontSize: 'var(--fs-200, 12px)' }}>
                              <strong>{c.clave ?? `Renglón ${c.refExterna}`}</strong> — {textoDeMotivo(c.motivo)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* La barra de reproducción */}
                <BarraDeReproduccion
                  rango={rango}
                  t={t}
                  reproduciendo={reproduciendo}
                  velocidad={velocidad}
                  deshabilitado={!modeloListo}
                  onAlternar={() => alternar(() => setConceptoElegido(null))}
                  onElegirFecha={saltarA}
                  onVelocidad={setVelocidad}
                />
              </>
            )}
          </DialogContent>
          <DialogActions style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button appearance="secondary" onClick={onCerrar}>
              Cerrar
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/**
 * Un concepto con su barra de avance a la fecha que se ve (pasa de 100 % solo cuando el contrato se rebasó). Si tiene piezas en el
 * modelo es un BOTÓN —con ícono, borde y resalte— que las marca en el dibujo; si no, es una fila de lectura que dice por qué.
 */
function FilaDeConcepto({ c, pct, elegido, onElegir }: { c: AvanceObraConcepto; pct: number; elegido: boolean; onElegir: () => void }) {
  const estilos = useEstilos();
  const tope = Math.max(0, Math.min(1, pct));
  const ubicable = c.piezas.length > 0;
  const contenido = (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 'var(--fs-200, 12px)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          {ubicable && (
            <span aria-hidden style={{ display: 'inline-flex', color: elegido ? 'var(--accent)' : 'var(--text-secondary)', flexShrink: 0 }}>
              {elegido ? <Location16Filled /> : <Location16Regular />}
            </span>
          )}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.descripcion ?? undefined}>
            {c.clave ? `${c.clave} · ` : ''}
            {c.descripcion ?? `Renglón ${c.refExterna}`}
          </span>
        </span>
        <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flexShrink: 0, color: pct > 1 + 1e-9 ? 'var(--warn)' : 'var(--text-secondary)' }}>
          {(pct * 100).toFixed(0)} %{c.excedido && pct > 1 + 1e-9 ? ' · rebasa el contrato' : ''}
        </span>
      </div>
      <div style={{ height: 6, background: 'var(--border-subtle)', borderRadius: 3, overflow: 'hidden', marginTop: 3 }}>
        <div style={{ width: `${tope * 100}%`, height: '100%', background: pct > 1 + 1e-9 ? 'var(--warn)' : 'var(--accent)' }} />
      </div>
    </>
  );
  if (!ubicable) {
    return <div style={{ padding: '2px 0' }}>{contenido}</div>;
  }
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={elegido}
      title={elegido ? 'Quitar la marca del modelo' : 'Ver sus piezas en el modelo'}
      className={estilos.filaUbicable}
      onClick={onElegir}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onElegir();
        }
      }}
      style={{
        padding: '6px 8px',
        borderRadius: 6,
        background: elegido ? 'var(--accent-weak)' : undefined,
        borderColor: elegido ? 'var(--accent)' : undefined,
      }}
    >
      {contenido}
    </div>
  );
}
